<template>
  <div class="stats-page">
    <div class="container container--fluid">
      <PageHeader
        :title="$t('stats_dashboard.title')"
        :description="periodSubtitle || $t('stats_dashboard.description')"
        break-mobile
      >
        <template #actions>
          <div class="stats-page__filters">
            <!-- Period dropdown -->
            <div class="stats-page__filter">
              <label class="stats-page__filter-label" for="stats-period">
                {{ $t('stats_dashboard.filters.period_label') }}
              </label>
              <select
                id="stats-period"
                class="stats-page__select"
                :value="filters.period"
                @change="handlePeriodChange($event.target.value)"
              >
                <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Pipeline dropdown (only when there is more than one) -->
            <div v-if="pipelines.length > 1" class="stats-page__filter">
              <label class="stats-page__filter-label" for="stats-pipeline">
                {{ $t('stats_dashboard.filters.pipeline_label') }}
              </label>
              <select
                id="stats-pipeline"
                class="stats-page__select"
                :value="filters.pipelineId || ''"
                @change="handlePipelineChange($event.target.value)"
              >
                <option value="">{{ $t('stats_dashboard.filters.pipeline_all') }}</option>
                <option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">
                  {{ pipeline.name }}
                </option>
              </select>
            </div>

            <Btn
              class="stats-page__refresh"
              variant="tertiary"
              size="sm"
              icon="redo"
              :loading="anyLoading && initialLoaded"
              :disabled="anyLoading && initialLoaded"
              @click="handleRefresh"
            >
              {{ $t('stats_dashboard.refresh') }}
            </Btn>
          </div>
        </template>
      </PageHeader>

      <!-- Custom date-range row, only shown when period === 'custom' -->
      <div v-if="filters.period === 'custom'" class="stats-page__custom-range">
        <div class="stats-page__custom-field">
          <label for="stats-date-from">
            {{ $t('stats_dashboard.filters.custom_from') }}
          </label>
          <input
            id="stats-date-from"
            type="date"
            :value="filters.dateFrom || ''"
            @change="handleCustomDateChange('dateFrom', $event.target.value)"
          />
        </div>
        <div class="stats-page__custom-field">
          <label for="stats-date-to">
            {{ $t('stats_dashboard.filters.custom_to') }}
          </label>
          <input
            id="stats-date-to"
            type="date"
            :value="filters.dateTo || ''"
            @change="handleCustomDateChange('dateTo', $event.target.value)"
          />
        </div>
      </div>

      <div class="stats-page__grid">
        <!-- ROW 1: KPI cards -->
        <StatsKpis :widget="widgets.kpis" @refresh="store.fetchWidget('kpis')" />

        <!-- ROW 2: Revenue timeline -->
        <StatsRevenueTimeline
          :widget="widgets.revenueTimeline"
          @refresh="store.fetchWidget('revenueTimeline')"
        />

        <!-- ROW 2b: Forecast — Prognose vs realisatie (Phase 3) -->
        <StatsForecast :widget="widgets.forecast" @refresh="store.fetchForecast()" />

        <!-- ROW 3: Funnel + Pipeline -->
        <!-- <StatsFunnel :widget="widgets.funnel" @refresh="store.fetchWidget('funnel')" /> -->
        <!-- <StatsPipeline :widget="widgets.pipeline" @refresh="store.fetchWidget('pipeline')" /> -->

        <!-- ROW 4: Health -->
        <!-- <StatsHealth :widget="widgets.health" @refresh="store.fetchWidget('health')" /> -->

        <!-- ROW 5: Products (mix-analyse) -->
        <!-- <StatsProducts :widget="widgets.products" @refresh="store.fetchWidget('products')" /> -->

        <!-- ROW 6–8: Marge — bewust uit; coverage is laag tot snapshots overal staan -->
        <!-- <h3 class="stats-page__group-heading">
          {{ $t('stats_dashboard.margin_group.title') }}
          <span class="stats-page__group-subtitle">
            {{ $t('stats_dashboard.margin_group.subtitle') }}
          </span>
        </h3>
        <StatsMarginTotals
          :widget="widgets.marginTotals"
          @refresh="store.fetchWidget('marginTotals')"
        />
        <StatsMarginProducts
          :widget="widgets.marginProducts"
          @refresh="store.fetchWidget('marginProducts')"
        />
        <StatsMarginUnderThreshold
          :widget="widgets.marginUnderThreshold"
          @refresh="store.fetchWidget('marginUnderThreshold')"
        /> -->

        <!-- ROW 9: Leaderboard -->
        <StatsLeaderboard
          :widget="widgets.leaderboard"
          @refresh="store.fetchWidget('leaderboard')"
        />

        <StatsRejectReasons
          :widget="widgets.rejectReasons"
          @refresh="store.fetchWidget('rejectReasons')"
        />

        <!-- ROW 10: Installer stats -->
        <!-- <StatsInstallerStats
          :widget="widgets.installerStats"
          @refresh="store.fetchWidget('installerStats')"
        /> -->
      </div>
    </div>
  </div>
</template>

<script>
  /**
   * Stats Dashboard page (entry point).
   *
   * Thin orchestrator: it owns nothing besides the wiring between the
   * dashboard-stats store and the widget components. All real logic
   * — fetching, filter cascading, error/loading state — lives in
   * `useDashboardStatsStore`.
   *
   * Layout follows §13 of the backend integration guide + the
   * 2026-05-12 margin & forecast deltas:
   *   1. KPI row              (full width)
   *   2. Revenue timeline     (full width)
   *   2b. Forecast (Phase 3)  (full width — Prognose vs realisatie)
   *   3. Funnel + Pipeline    (50/50)
   *   4. Health               (full width)
   *   5. Products             (full width)
   *   — "Brutomarge" section heading —
   *   6. Margin totals        (full width)
   *   7. Margin per product   (full width, best/worst side-by-side)
   *   8. Under-threshold list (full width, table)
   *   9. Leaderboard          (full width, 2 internal panels)
   *  10. Installer stats      (full width)
   */
  import { computed, onMounted, getCurrentInstance } from 'vue';
  import { storeToRefs } from 'pinia';

  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';

  import StatsKpis from '@/components/stats/StatsKpis.vue';
  import StatsRevenueTimeline from '@/components/stats/StatsRevenueTimeline.vue';
  import StatsFunnel from '@/components/stats/StatsFunnel.vue';
  import StatsPipeline from '@/components/stats/StatsPipeline.vue';
  import StatsHealth from '@/components/stats/StatsHealth.vue';
  import StatsLeaderboard from '@/components/stats/StatsLeaderboard.vue';
  import StatsRejectReasons from '@/components/stats/StatsRejectReasons.vue';
  import StatsInstallerStats from '@/components/stats/StatsInstallerStats.vue';
  import StatsProducts from '@/components/stats/StatsProducts.vue';
  import StatsMarginTotals from '@/components/stats/StatsMarginTotals.vue';
  import StatsMarginProducts from '@/components/stats/StatsMarginProducts.vue';
  import StatsMarginUnderThreshold from '@/components/stats/StatsMarginUnderThreshold.vue';
  import StatsForecast from '@/components/stats/StatsForecast.vue';

  import { useDashboardStatsStore } from '@/store/dashboardStatsStore';
  import { useDealStore } from '@/store/dealStore';
  import { useToastStore } from '@/store/toastStore';
  import { formatPeriodRange } from '@/utils/statsFormat';

  const PERIOD_VALUES = ['7d', '30d', '90d', 'mtd', 'qtd', 'ytd', 'all', 'custom'];

  export default {
    name: 'StatsPage',

    components: {
      PageHeader,
      Btn,
      StatsKpis,
      StatsRevenueTimeline,
      StatsFunnel,
      StatsPipeline,
      StatsHealth,
      StatsLeaderboard,
      StatsRejectReasons,
      StatsInstallerStats,
      StatsProducts,
      StatsMarginTotals,
      StatsMarginProducts,
      StatsMarginUnderThreshold,
      StatsForecast,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const store = useDashboardStatsStore();
      const dealStore = useDealStore();
      const toastStore = useToastStore();

      const { filters, widgets, periodStart, periodEnd, anyLoading, initialLoaded } =
        storeToRefs(store);

      const pipelines = computed(() => dealStore.pipelines || []);

      const periodOptions = computed(() =>
        PERIOD_VALUES.map((value) => ({
          value,
          label: $t(`stats_dashboard.periods.${value}`),
        }))
      );

      // Localised "12 apr — 12 mei · Bedragen excl. BTW" subtitle.
      // The VAT note is always appended once the first response is
      // back — it's a tenant-facing reminder that the dashboard
      // numbers are now excl. VAT (changed 2026-05-12) so they line
      // up with /api/crm/proposals/stats.
      const periodSubtitle = computed(() => {
        const range = formatPeriodRange(periodStart.value, periodEnd.value);
        const vatNote = $t('stats_dashboard.vat_note');
        if (!range) return '';
        return `${range} · ${vatNote}`;
      });

      const handlePeriodChange = async (period) => {
        if (period === 'custom') {
          // Wait for the user to pick both dates before fetching.
          store.filters.period = 'custom';
          return;
        }
        await reload(() => store.setPeriod(period));
      };

      const handlePipelineChange = async (pipelineId) => {
        await reload(() => store.setPipelineId(pipelineId || null));
      };

      const handleCustomDateChange = async (key, value) => {
        if (key === 'dateFrom') store.filters.dateFrom = value || null;
        if (key === 'dateTo') store.filters.dateTo = value || null;
        // Only fire the request once we have both ends, otherwise the
        // backend rejects the period=custom request with a 400.
        if (store.filters.dateFrom && store.filters.dateTo) {
          await reload(() => store.fetchAll({ force: true }));
        }
      };

      const handleRefresh = async () => {
        await reload(() => store.fetchAll({ force: true }));
      };

      const reload = async (action) => {
        try {
          await action();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('stats_dashboard.load_failed'),
          });
          // We swallow the error after toasting — every widget already
          // shows its own error state, so there's nothing else for
          // the page to do.
          // eslint-disable-next-line no-console
          console.error('[stats-dashboard] reload failed', error);
        }
      };

      onMounted(async () => {
        // Fire pipelines + dashboard in parallel — the pipeline filter
        // dropdown isn't a blocker for the first paint, so we don't
        // await it before the stats request.
        const tasks = [reload(() => store.fetchAll({ force: false }))];
        if (!dealStore.pipelines.length && !dealStore.pipelinesLoading) {
          tasks.push(
            dealStore.fetchPipelines().catch((error) => {
              // Pipelines are nice-to-have for the filter, not the
              // dashboard data itself — log and move on.
              // eslint-disable-next-line no-console
              console.warn('[stats-dashboard] could not load pipelines', error);
            })
          );
        }
        await Promise.all(tasks);
      });

      return {
        store,
        filters,
        widgets,
        anyLoading,
        initialLoaded,
        pipelines,
        periodOptions,
        periodSubtitle,
        handlePeriodChange,
        handlePipelineChange,
        handleCustomDateChange,
        handleRefresh,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-page {
    padding-bottom: 2rem;

    &__filters {
      display: flex;
      align-items: end;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-bottom: 0.875rem;

      @include lg-down {
        align-items: stretch;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
      }
    }

    &__filter {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      @include lg-down {
        width: 100%;
      }
    }

    &__filter-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    &__select {
      appearance: none;
      -webkit-appearance: none;
      background:
        url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")
          no-repeat right 0.625rem center / 10px 10px,
        var(--slate-0, #fff);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      min-height: 2.5rem;
      padding: 0.5rem 1.875rem 0.5rem 0.75rem;
      font-size: 0.8125rem;
      line-height: 1.2;
      color: var(--slate-800);
      font-weight: 500;
      min-width: 9rem;
      cursor: pointer;
      transition:
        border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
        background-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
        box-shadow 0.2s cubic-bezier(0.22, 1, 0.36, 1);

      @include lg-down {
        width: 100%;
        min-width: 0;
      }

      @include hover {
        border-color: var(--slate-300);
        background: var(--slate-25, #fcfcfd);
      }

      &:focus {
        outline: none;
        border-color: var(--brand-400);
        box-shadow: 0 0 0 3px rgb(59 130 246 / 0.15);
      }

      &:disabled {
        cursor: not-allowed;
        color: var(--slate-400);
        background: var(--slate-100);
      }
    }

    &__custom-range {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
      padding: 0.875rem;
      margin-bottom: 1rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.625rem;

      @include lg-down {
        gap: 0.5rem;
        padding: 0.75rem;
      }
    }

    &__custom-field {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      min-width: 9rem;

      @include lg-down {
        width: 100%;
        min-width: 0;
      }

      label {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--slate-500);
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }

      input {
        min-height: 2.5rem;
        padding: 0.5rem 0.625rem;
        font-size: 0.8125rem;
        line-height: 1.2;
        border: 1px solid var(--slate-200);
        border-radius: 0.5rem;
        background: var(--slate-0, #fff);
        color: var(--slate-800);
        transition:
          border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
          background-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
          box-shadow 0.2s cubic-bezier(0.22, 1, 0.36, 1);

        @include hover {
          border-color: var(--slate-300);
          background: var(--slate-25, #fcfcfd);
        }

        &:focus {
          outline: none;
          border-color: var(--brand-400);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.15);
        }
      }
    }

    &__refresh {
      min-height: 2.5rem;
      align-self: end;
      margin-left: 0.125rem;

      @include lg-down {
        width: 100%;
        margin-left: 0;
      }
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1.125rem;

      @include lg-down {
        grid-template-columns: 1fr;
        gap: 1rem;
      }
    }

    // Section divider for grouped widget blocks (e.g. "Marge").
    // The heading spans both grid columns so the widgets below it
    // visually belong to the same section.
    &__group-heading {
      grid-column: span 2;
      margin: 0.75rem 0 -0.25rem;
      display: flex;
      align-items: baseline;
      gap: 0.625rem;
      font-size: 0.8125rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--slate-600);

      @include lg-down {
        grid-column: span 1;
      }
    }

    &__group-subtitle {
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-400);
      letter-spacing: 0.02em;
      text-transform: none;
    }
  }
</style>
